import type { Inputs, Scale5, PartnerStatus, Experience, Dose } from '../data/types';
import { DOSE_LABELS, DOSE_ORDER } from '../data/types';
import styles from './InputsPanel.module.css';

interface InputsPanelProps {
  inputs: Inputs;
  onChange: (next: Inputs) => void;
}

const SCALE_5_LABELS: Record<number, string> = {
  1: 'not much',
  2: 'a little',
  3: 'moderate',
  4: 'a lot',
  5: 'very much',
};

const PARTNER_OPTIONS: { value: PartnerStatus; label: string }[] = [
  { value: 'solo', label: 'solo' },
  { value: 'dating', label: 'dating' },
  { value: 'vanilla-partner', label: 'vanilla partner' },
  { value: 'kinky-partner', label: 'kinky partner' },
];

const EXPERIENCE_OPTIONS: { value: Experience; label: string }[] = [
  { value: 'none', label: 'none yet' },
  { value: 'some-scenes', label: 'some scenes' },
  { value: 'ongoing-dynamic', label: 'ongoing dynamic' },
];

export function InputsPanel({ inputs, onChange }: InputsPanelProps) {
  function set<K extends keyof Inputs>(key: K, value: Inputs[K]) {
    onChange({ ...inputs, [key]: value });
  }

  function slider(
    key: keyof Inputs,
    label: string,
    hint: string,
    value: Scale5
  ) {
    return (
      <div className={styles.row}>
        <div className={styles.rowHead}>
          <label htmlFor={`s-${key}`} className={styles.rowLabel}>
            {label}
          </label>
          <span className={styles.rowValue}>{SCALE_5_LABELS[value]}</span>
        </div>
        <input
          id={`s-${key}`}
          type="range"
          min={1}
          max={5}
          step={1}
          value={value}
          onChange={(e) => set(key, Number(e.target.value) as Scale5)}
          className={styles.slider}
          aria-valuetext={SCALE_5_LABELS[value]}
        />
        <p className={styles.rowHint}>{hint}</p>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      {slider(
        'lifeFullness',
        'life fullness',
        'how full is your life right now (work, family, projects)?',
        inputs.lifeFullness
      )}
      {slider(
        'energy',
        'available energy',
        'after the unavoidable things, how much do you have left?',
        inputs.energy
      )}

      <div className={styles.row}>
        <div className={styles.rowHead}>
          <label className={styles.rowLabel}>partner status</label>
        </div>
        <div className={styles.chips}>
          {PARTNER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`${styles.chip} ${inputs.partnerStatus === opt.value ? styles.chipActive : ''}`}
              onClick={() => set('partnerStatus', opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className={styles.rowHint}>
          this affects the most important variable for higher doses.
        </p>
      </div>

      <div className={styles.row}>
        <div className={styles.rowHead}>
          <label htmlFor="hours" className={styles.rowLabel}>
            hours per week available
          </label>
          <span className={styles.rowValue}>{inputs.hoursPerWeek}h</span>
        </div>
        <input
          id="hours"
          type="range"
          min={0}
          max={40}
          step={1}
          value={inputs.hoursPerWeek}
          onChange={(e) => set('hoursPerWeek', Number(e.target.value))}
          className={styles.slider}
        />
        <p className={styles.rowHint}>
          time you can realistically give — talking, scenes, attention, ritual.
        </p>
      </div>

      {slider(
        'supportSystems',
        'support systems',
        'community, friends in the know, therapy if helpful.',
        inputs.supportSystems
      )}

      <div className={styles.row}>
        <div className={styles.rowHead}>
          <label className={styles.rowLabel}>previous experience</label>
        </div>
        <div className={styles.chips}>
          {EXPERIENCE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`${styles.chip} ${inputs.experience === opt.value ? styles.chipActive : ''}`}
              onClick={() => set('experience', opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {slider(
        'lifeStability',
        'life stability',
        'housing, finances, mental health — how steady is the ground?',
        inputs.lifeStability
      )}
      {slider(
        'riskTolerance',
        'risk tolerance',
        'psychologically, not physically. how much intensity can you metabolize?',
        inputs.riskTolerance
      )}
      {slider(
        'curiosityVsCommitment',
        'curiosity vs. commitment',
        '1 = explore, 5 = commit. where are you on that arc right now?',
        inputs.curiosityVsCommitment
      )}

      <div className={styles.row}>
        <div className={styles.rowHead}>
          <label className={styles.rowLabel}>smallest dose worth doing</label>
        </div>
        <div className={styles.chips}>
          {DOSE_ORDER.map((d: Dose) => (
            <button
              key={d}
              type="button"
              className={`${styles.chip} ${inputs.desiredMinDose === d ? styles.chipActive : ''}`}
              onClick={() => set('desiredMinDose', d)}
            >
              {DOSE_LABELS[d]}
            </button>
          ))}
        </div>
        <p className={styles.rowHint}>
          the calibrator never recommends less than this.
        </p>
      </div>
    </div>
  );
}
